import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { CALENDLY_URL } from "@/content";
import Reveal from "@/components/Reveal";
import ShareBar from "@/components/ShareBar";
import { INSIGHTS, getInsight } from "@/insights";

export default function InsightArticle() {
  const { slug } = useParams();
  const article = getInsight(slug);
  if (!article) return <Navigate to="/insights" replace />;

  const idx = INSIGHTS.indexOf(article);
  const next = INSIGHTS[(idx + 1) % INSIGHTS.length];

  return (
    <article data-testid="insight-article-page">
      <SEO title={`${article.title} | Thrive Insights`} description={article.dek} />

      <header className="relative overflow-hidden pt-36 pb-16 sm:pt-44 sm:pb-20" data-testid="insight-article-hero">
        <div className="absolute inset-0 dot-grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link
              to="/insights"
              data-testid="insight-back-link"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-gold"
            >
              <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" />
              All Insights
            </Link>
            <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
              <span className="text-gold">{article.category}</span>
              <span>{article.date}</span>
              <span>{article.readTime}</span>
            </div>
            <h1 className="mt-6 font-display max-w-4xl text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
              {article.title}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">{article.dek}</p>
          </Reveal>
        </div>
      </header>

      <section className="border-t border-white/10 py-16 lg:py-24" data-testid="insight-article-body">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-[220px_1fr] lg:gap-20">
          <Reveal>
            <div className="lg:sticky lg:top-32">
              <span className="font-mono text-4xl text-white/15">{article.num}</span>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                Thrive Commentary
              </p>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                Written by the Thrive Business Development team.
              </p>
              <div className="mt-8 border-t border-white/10 pt-6">
                <ShareBar title={article.title} variant="column" />
              </div>
            </div>
          </Reveal>

          <div className="max-w-2xl">
            {article.body.map((para, i) => (
              <Reveal key={i} delay={0.04}>
                <p className="prose-justify mb-7 text-base sm:text-[17px] text-slate-300 leading-[1.8]">{para}</p>
                {i === 1 && (
                  <blockquote
                    className="my-12 border-l-2 border-gold/70 pl-7 font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] leading-tight text-white"
                    data-testid="insight-pull-quote"
                  >
                    {article.pull}
                  </blockquote>
                )}
              </Reveal>
            ))}
            <Reveal>
              <div className="mt-12 border-t border-white/10 pt-8" data-testid="insight-article-share-end">
                <ShareBar title={article.title} variant="inline" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20" data-testid="insight-next">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow-blue mb-6">Next Commentary</p>
            <Link
              to={`/insights/${next.slug}`}
              data-testid="insight-next-link"
              className="group flex flex-col gap-4 sm:flex-row sm:items-baseline sm:justify-between"
            >
              <h2 className="font-display max-w-3xl text-2xl sm:text-3xl font-bold tracking-[-0.03em] leading-tight text-white transition-colors duration-300 group-hover:text-gold">
                {next.title}
              </h2>
              <span className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-electric transition-colors group-hover:text-white">
                Read
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="insight-article-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <Reveal>
            <h2 className="font-display max-w-xl text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">
              Talk to us about your market.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="insight-book-call-button"
              className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              Book a Call
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </article>
  );
}
